import React, { useEffect } from 'react';
import DialogKit from './dialogKit';
import ScrollCue from '../grid/scrollCue';

// New UI only: gives Classic's unnamed controls an accessible name, from what the screen already shows. Nothing is
// renamed: an element that has a name (text, aria-label, aria-labelledby, a <label for>) is left alone.
// - Icon buttons: the label the new UI draws for them (CSS ::after, e.g. "Add line"), else their tooltip text (MUI puts
//   it on the icon as aria-label), an image's alt, or a title.
// - Fields: the label shown beside them in Classic's form rows (.row-form, .form-group, label/field columns).
// - MUI selects: the same, or what they filter ("Period", "Routed to").
// - Grid selection checkboxes: "Select row" / "Select all rows".

const text = el => (el && el.textContent ? el.textContent.replace(/\s+/g, ' ').trim() : '');
const clean = s => (s || '').replace(/[:*]+$/g, '').replace(/^\*+/, '').replace(/\s+/g, ' ').trim();

function hasName(el) {
	if (!el) return true;
	if ((el.getAttribute('aria-label') || '').trim()) return true;
	const by = (el.getAttribute('aria-labelledby') || '').split(/\s+/).filter(id => id && id !== el.id);
	if (by.some(id => text(el.ownerDocument.getElementById(id)))) return true; // MUI points at ids that may not exist
	if (el.id) {
		try { if (el.ownerDocument.querySelector(`label[for="${CSS.escape(el.id)}"]`)) return true; } catch (e) { /* odd id */ }
	}
	if (el.closest('label')) return true;
	return false;
}

function pseudoLabel(el) {
	try {
		const c = window.getComputedStyle(el, '::after').content;
		if (c && c !== 'none' && c !== 'normal') return c.replace(/^["']|["']$/g, '');
	} catch (e) { /* jsdom has no pseudo elements */ }
	return '';
}

// Icon-only buttons whose icon says what they do (MUI icons carry data-testid="<Name>Icon").
const ICONS = {
	CheckIcon: 'Save', DoneIcon: 'Save', ClearIcon: 'Clear', SearchIcon: 'Search', EditIcon: 'Edit', DeleteIcon: 'Delete',
	DeleteOutlineIcon: 'Delete', DeleteOutlinedIcon: 'Delete', AddBoxIcon: 'Add', AddIcon: 'Add', RefreshIcon: 'Refresh',
	MoreHorizIcon: 'More', MoreVertIcon: 'More', FileDownloadIcon: 'Download', VisibilityIcon: 'View'
};
function iconName(btn) {
	const icon = btn.querySelector('svg[data-testid]');
	const id = icon && icon.getAttribute('data-testid');
	if (id === 'CloseIcon') return btn.closest('.MuiDialogTitle-root, .MuiDialog-paper > :first-child') ? 'Close' : 'Cancel';
	if (id === 'SearchIcon') { // a picker inside a grid cell: "Choose vendor code"
		const cell = btn.closest('.MuiDataGrid-cell[data-field]');
		const grid = cell && cell.closest('.MuiDataGrid-root');
		const header = grid && grid.querySelector(`.MuiDataGrid-columnHeader[data-field="${cell.getAttribute('data-field')}"]`);
		const h = header && clean(header.getAttribute('aria-label') || text(header));
		if (h) return `Choose ${h.toLowerCase()}`;
	}
	return (id && ICONS[id]) || '';
}

// Classic copied aria-label="Field Source" onto buttons that aren't the field-source button (that one has the
// LineStyle icon): the settings "•••" of Model setup fields, the approval stamp, the IO/PO search on a line and the
// user-group picker. They are named from what they do, in context.
export function fieldSourceName(btn) {
	if ((btn.getAttribute('aria-label') || '') !== 'Field Source' || btn.querySelector('svg[data-testid="LineStyleIcon"]')) return '';
	if (/^search-io-po/.test(btn.id || '')) {
		const tr = btn.closest('tr, .MuiDataGrid-row');
		const rows = tr && tr.parentElement ? [...tr.parentElement.children].filter(r => r.matches('tr, .MuiDataGrid-row')) : [];
		return rows.length ? `Find IO/PO for line ${rows.indexOf(tr) + 1}` : 'Find IO/PO';
	}
	if (btn.querySelector('svg[data-testid="EditLocationIcon"]')) return 'Place the approval stamp';
	if (btn.closest('.MuiDataGrid-cell')) return 'Choose user groups';
	const row = btn.closest('.row-form, .form-group, .row');
	const label = row ? clean(text(row.querySelector('label, .labeldiv'))) : '';
	if (/approval stamp/i.test(label)) return 'Approval stamp settings';
	let input = null;
	for (let el = btn.parentElement; el && el !== row && !input; el = el.parentElement) {
		const inputs = el.querySelectorAll('input');
		if (inputs.length === 1) input = inputs[0];
	}
	const part = input && /label$/i.test(input.name || '') ? ' label' : input && /value$/i.test(input.name || '') ? ' value' : '';
	return label ? `Settings for ${label}${part}` : 'Settings';
}

export function buttonName(btn) {
	const wrong = fieldSourceName(btn);
	if (wrong) return wrong;
	if (hasName(btn) || text(btn)) return '';
	const fromCss = pseudoLabel(btn);
	if (fromCss) return fromCss;
	const svg = btn.querySelector('svg[aria-label], svg[title]');
	if (svg) return svg.getAttribute('aria-label') || svg.getAttribute('title');
	const img = btn.querySelector('img[alt]');
	if (img && img.alt) return img.alt.replace(/\s*icon$/i, '');
	const wrap = btn.parentElement && btn.parentElement.getAttribute('aria-label'); // MUI Tooltip around a disabled button
	if (wrap) return wrap;
	return btn.getAttribute('title') || iconName(btn);
}

const ROW = '.row-form, .form-group, .row, .MuiGrid-item, .MuiBox-root, tr';
export function fieldName(field) {
	if (hasName(field)) return '';
	const ph = field.getAttribute('placeholder');
	if (ph && !/^[\d.,\s$%-]+$/.test(ph)) return ph; // "0.00" is a format hint, not a name
	// In a grid cell: the column's header ("Tax Code").
	const cell = field.closest('.MuiDataGrid-cell[data-field]');
	if (cell) {
		const grid = cell.closest('.MuiDataGrid-root');
		const header = grid && grid.querySelector(`.MuiDataGrid-columnHeader[data-field="${cell.getAttribute('data-field')}"]`);
		const h = header && clean(header.getAttribute('aria-label') || text(header));
		if (h) return h;
	}
	const isCheck = field.type === 'checkbox' || field.type === 'radio';
	// The nearest label-like text before the field in its row (Classic puts the label first, then the field).
	const row = field.closest(ROW);
	if (row) {
		const labels = [...row.querySelectorAll('label, .labeldiv, .MuiTypography-root')].filter(l => !l.contains(field));
		const pick = list => list.map(l => clean(text(l))).filter(t => t && t.length <= 60);
		const before = pick(labels.filter(l => l.compareDocumentPosition(field) & Node.DOCUMENT_POSITION_FOLLOWING));
		const after = pick(labels.filter(l => l.compareDocumentPosition(field) & Node.DOCUMENT_POSITION_PRECEDING));
		// A checkbox's label usually follows it; a text field's label comes first.
		if (isCheck && after.length) return after[0];
		if (before.length) return before[before.length - 1];
		if (after.length) return after[0];
	}
	const name = field.getAttribute('name') || '';
	if (/^weeks-to-filter/.test(name)) return 'Period';
	if (name === 'routed-to-filter') return 'Routed to';
	if (/\b(\d+ weeks?|no filter)\b/i.test(text(field.closest('.MuiInputBase-root')) || '')) return 'Period';
	return humanize(field.id) || humanize(name);
}

// Last resort: a descriptive id or name ("txtCompanyCode", "tax-code-edit-1") read as words ("Company code", "Tax code").
export function humanize(id) {
	// A prefix is only stripped before a separator or a capital ("selectLine" keeps "select"; "selTax" loses "sel").
	const words = (id || '').replace(/^(txt|ddl|chk|lbl|cbo|sel|input|mui-component-select)(?:[-_](?=[A-Za-z])|(?=[A-Z]))/, '')
		.replace(/[-_]?\d+$/, '').replace(/[-_](edit|input|field|select)$/i, '')
		.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2').replace(/[-_]+/g, ' ').trim();
	if (!words || words.length < 3 || /^(mui|r|input|field)\b/i.test(words) || /:/.test(id)) return '';
	return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase().replace(/\bio\b/g, 'IO').replace(/\bpo\b/g, 'PO');
}

// The editor's search icons are <img> elements with a click handler: they become keyboard buttons (Enter and Space
// click them, see A11yNames), named "Find vendor" and so on.
export function searchIcons(root) {
	let n = 0;
	root.querySelectorAll('img[alt^="Search" i]:not([role])').forEach(img => {
		img.setAttribute('role', 'button');
		img.setAttribute('tabindex', '0');
		img.setAttribute('aria-label', img.alt.replace(/^search\s*/i, 'Find ').replace(/\s+(\w)/g, (m, c) => ' ' + c.toLowerCase()));
		n++;
	});
	return n;
}

// Classic's positive tabIndex values in the editor jump around the new layout; the new UI tabs in reading order.
export function flattenTabOrder(root) {
	let n = 0;
	root.querySelectorAll('.apw2-ed [tabindex]').forEach(el => { if (Number(el.getAttribute('tabindex')) > 0) { el.setAttribute('tabindex', '0'); n++; } });
	return n;
}

export function nameControls(root) {
	if (!root) return 0;
	let n = searchIcons(root) + flattenTabOrder(root);
	const set = (el, name) => { if (name && el.getAttribute('aria-label') !== name) { el.setAttribute('aria-label', name); n++; } };
	root.querySelectorAll('button, [role="button"]:not(img)').forEach(b => set(b, buttonName(b)));
	root.querySelectorAll('input:not([type="hidden"]):not(.MuiSelect-nativeInput), select, textarea').forEach(f => {
		if (f.type === 'checkbox' && f.closest('.MuiDataGrid-root') && (f.closest('.MuiDataGrid-row') || f.closest('.MuiDataGrid-columnHeader'))) {
			if (!hasName(f)) set(f, f.closest('.MuiDataGrid-columnHeader') ? 'Select all rows' : 'Select row');
			return;
		}
		set(f, fieldName(f));
	});
	root.querySelectorAll('[role="combobox"].MuiSelect-select').forEach(c => {
		if (hasName(c)) return;
		const input = c.parentElement && c.parentElement.querySelector('input.MuiSelect-nativeInput');
		set(c, fieldName(input || c) || (input && fieldName(c)));
	});
	return n;
}

// Watches the page and dialogs (MUI portals under <body>). The shell mounts this once, so it also brings the popup
// helper (dialogKit.js: Escape, ✕, button order, focus) and the grids' scroll cue (grid/scrollCue.js).
export function A11yNames() {
	useEffect(() => {
		let frame = 0;
		const run = () => { frame = 0; nameControls(document.body); };
		run();
		const observer = new MutationObserver(() => { if (!frame) frame = requestAnimationFrame(run); });
		observer.observe(document.body, { childList: true, subtree: true });
		const onKey = e => {
			if ((e.key === 'Enter' || e.key === ' ') && e.target && e.target.matches && e.target.matches('img[role="button"]')) { e.preventDefault(); e.target.click(); }
		};
		document.addEventListener('keydown', onKey);
		return () => { observer.disconnect(); if (frame) cancelAnimationFrame(frame); document.removeEventListener('keydown', onKey); };
	}, []);
	return <><DialogKit /><ScrollCue /></>;
}
